import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import lodash from 'lodash';
import cx from 'classnames';

import { getAllRolesThunk, updateUserList } from '../../store/user/userSlice';
import {
  buildQueryString,
  findSearchValue,
  getFinishPreviewUserId,
  getUserListPagePaginationLimit,
  parseQueryString,
  removeFinishPreviewUserId,
  setUserListPagePaginationLimit,
} from '../../helpers';
import { OrganizationUsersHeader, SAOrganizationUser } from '../modules/Users';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ContainedButton, RolesButton } from '../modules/Buttons';
import { PaginationFooter } from '../modules/PaginationFooter';
import CreateSuperAdminModal from './CreateSuperAdminModal';
import { ROLES, RolesLowerCase } from '../../enums/ROLES';
import BellIcon from '../../assets/icons/bell-button.svg';
import { SortFields, SortType } from '../../enums/SORT';
import LinearProgress from '../modules/LinearProgress';
import SearchInput from '../modules/SearchInput';
import CreateUserModal from './CreateUserModal';
import styles from './UsersList.module.scss';
import Divider from '../modules/Divider';
import { getAllUsers, postResendRegistrationLink, postUserHelpResponse } from '../../api';
import FinishPreviewModal from '../modules/Users/FinishPreviewModal';
import FullPageLoader from '../modules/FullPageLoader';
import { USER } from '../../enums';
import RenotifyUserModal from '../UsersList/RenotifyUserModal';
import { enqueueSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';
import useDebounce from '../../helpers/useDebounce';

enum ROLE_OPTIONS {
  ALL = 'All',
}

const SAUsersList = () => {
  const dispatch = useAppDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const switchModal = () => {
    setShowAddModal(!showAddModal);
  };
  const finishPreview = getFinishPreviewUserId();

  const [showFinishPreviewModal, setShowFinishPreviewModal] = useState(!!finishPreview);

  const [currentRole, setCurrentRole] = useState<RolesLowerCase | ROLE_OPTIONS>(ROLE_OPTIONS.ALL);
  const [searchText, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<SortFields>(SortFields.NAME);
  const [sortType, setSortType] = useState<SortType>(SortType.ASC);
  const [total, setTotal] = useState<number>(0);

  const { role: userRole, userList } = useAppSelector((state) => state.user);

  const searchedName = findSearchValue('email');

  const [fullPageLoader, setFullPageLoader] = useState(false);
  useEffect(() => {
    if (searchedName?.length) {
      setFullPageLoader(true);
      setTimeout(() => {
        setFullPageLoader(false);
        setSearch(searchedName);
      }, 4000);
    }
  }, []);

  useEffect(() => {
    const prefferedLimit = getUserListPagePaginationLimit();
    if (prefferedLimit && lodash.isNumber(+prefferedLimit)) {
      setRowsPerPage(+prefferedLimit);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);

    let rolesQuery = '';

    if (currentRole === ROLE_OPTIONS.ALL) {
      rolesQuery = `${RolesLowerCase.ORGANIZATION_ADMIN}&role=${RolesLowerCase.ANALYST}&role=${RolesLowerCase.VIEWER}&role=${RolesLowerCase.SUPER_ADMIN}`;
    } else {
      rolesQuery = `${currentRole}`;
    }

    if (helpFilter) {
      rolesQuery = `${rolesQuery}&need_help=true`;
    }

    try {
      if (userRole === ROLES.SUPER_ADMIN) {
        const res = await getAllUsers(page, rowsPerPage, searchText, sortType, rolesQuery, sortField);
        dispatch(updateUserList(res.data.documents));
        setTotal(res.data.total);
        return res;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const switchFinishPreviewModal = () => {
    setShowFinishPreviewModal(!showFinishPreviewModal);
  };

  const handleResolvedIssue = async (responseText: string) => {
    try {
      finishPreview && (await postUserHelpResponse({ userId: +finishPreview, successfulResponse: true, responseText }));
    } catch (error) {
      console.error(error);
    }
    setShowFinishPreviewModal(!showFinishPreviewModal);
    removeFinishPreviewUserId();
    fetchData();
  };

  const handleNotResolvedIssue = async (responseText: string) => {
    try {
      finishPreview && (await postUserHelpResponse({ userId: +finishPreview, successfulResponse: false, responseText }));
    } catch (error) {
      console.error(error);
    }
    setShowFinishPreviewModal(!showFinishPreviewModal);
    removeFinishPreviewUserId();
  };

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, []);

  const [helpFilter, setHelpFilter] = useState<'need_help' | null>(null);
  const chooseHelpFilter = () => {
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setHelpFilter(helpFilter ? null : 'need_help');
  };

  const searchDebounce = useDebounce<string>(searchText, 1000);
  const { notificationTrigger } = useAppSelector((state) => state.sharedStore);
  useEffect(() => {
    fetchData();
  }, [userRole, searchDebounce, currentRole, page, rowsPerPage, sortField, sortType, helpFilter, notificationTrigger]);

  const setRoleFilter = (role: RolesLowerCase | ROLE_OPTIONS) => {
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setCurrentRole(role);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleLowerCase();
    setPage(0);
    if (searchedName) {
      const paramsObject = parseQueryString(window.location.search);
      if (paramsObject?.email) {
        delete paramsObject.email;
        window.history.replaceState(null, '', `?${buildQueryString(paramsObject)}`);
      }
    }

    setSearch(value);
  };

  const sortByType = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const target = e.currentTarget as HTMLButtonElement;
    if (target.name) {
      setSortField(target.name as SortFields);
      setSortType(sortType === SortType.ASC ? SortType.DESC : SortType.ASC);
    }
  };

  const onPaginate = (_: any, newPage: number) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const listRef = useRef<HTMLDivElement>(null);

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setUserListPagePaginationLimit(newRowsPerPage);
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const [showAddSuperAdminModal, setShowSuperAdminModal] = useState(false);
  const switchAddSuperAdminModal = () => {
    setShowSuperAdminModal(!showAddSuperAdminModal);
  };

  const [renotifyUsers, setRenotifyUsers] = useState<USER[] | 'all' | null>(null);
  const toRenotifyUserModal = (val: USER[] | 'all' | null) => {
    setRenotifyUsers(val);
  };

  const closeRenotifyModal = () => {
    setRenotifyUsers(null);
  };

  const lastResendAllTime = localStorage.getItem('last_resend_sa');
  const [disableSendAll, setDisableSendAll] = useState(false);

  useEffect(() => {
    if (lastResendAllTime) {
      const lastResend = new Date(localStorage.getItem('last_resend_sa')!);
      const isWithin24Hours = Date.now() - lastResend.getTime() < 24 * 60 * 60 * 1000;
      if (isWithin24Hours) {
        setDisableSendAll(true);
      }
    }
  }, [lastResendAllTime]);
  const renotifyAll = async () => {
    setLoading(true);

    try {
      const res = await postResendRegistrationLink({ all: true });
      if (res?.data?.emailsSentTo) {
        enqueueSnackbar(`Links are sent to ${res.data.emailsSentTo} user(s)`, {
          preventDuplicate: true,
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      } else {
        enqueueSnackbar(`0 emails was sent`, {
          preventDuplicate: true,
          variant: 'warning',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      }

      localStorage.setItem('last_resend_sa', `${new Date()}`);
      await fetchData();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.link}>
        <div className={styles.linkEl}>Users List</div>
      </div>
      <Divider />
      {loading && <LinearProgress />}
      {fullPageLoader && <FullPageLoader loading />}
      <div className="mb-5">
        <div className={styles.pageTitle}>Users List</div>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <SearchInput className={styles.search} onChange={onSearch} value={searchText} />
            <div className={styles.toolbarRoles}>
              <RolesButton
                kind={currentRole === ROLE_OPTIONS.ALL ? 'white' : 'gray'}
                hover={currentRole !== ROLE_OPTIONS.ALL ? 'white' : 'none'}
                onClick={() => setRoleFilter(ROLE_OPTIONS.ALL)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)' }}
              >
                <div className={styles.roleButton}>All {currentRole === ROLE_OPTIONS.ALL && <CheckIcon sx={{ height: '12px' }} />}</div>
              </RolesButton>
              {userRole === ROLES.SUPER_ADMIN && (
                <RolesButton
                  kind={currentRole === RolesLowerCase.SUPER_ADMIN ? 'mint' : 'gray'}
                  hover={currentRole !== RolesLowerCase.SUPER_ADMIN ? 'mint' : 'none'}
                  onClick={() => setRoleFilter(RolesLowerCase.SUPER_ADMIN)}
                  customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>
                    Super Admin {currentRole === RolesLowerCase.SUPER_ADMIN && <CheckIcon sx={{ height: '12px' }} />}
                  </div>
                </RolesButton>
              )}

              <RolesButton
                kind={currentRole === RolesLowerCase.ORGANIZATION_ADMIN ? 'orange' : 'gray'}
                hover={currentRole !== RolesLowerCase.ORGANIZATION_ADMIN ? 'orange' : 'none'}
                onClick={() => setRoleFilter(RolesLowerCase.ORGANIZATION_ADMIN)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>
                  OA {currentRole === RolesLowerCase.ORGANIZATION_ADMIN && <CheckIcon sx={{ height: '12px' }} />}
                </div>
              </RolesButton>
              <RolesButton
                kind={currentRole === RolesLowerCase.ANALYST ? 'blue' : 'gray'}
                hover={currentRole !== RolesLowerCase.ANALYST ? 'blue' : 'none'}
                onClick={() => setRoleFilter(RolesLowerCase.ANALYST)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>
                  Analyst {currentRole === RolesLowerCase.ANALYST && <CheckIcon sx={{ height: '12px' }} />}
                </div>
              </RolesButton>
              <RolesButton
                kind={currentRole === RolesLowerCase.VIEWER ? 'green' : 'gray'}
                hover={currentRole !== RolesLowerCase.VIEWER ? 'green' : 'none'}
                onClick={() => setRoleFilter(RolesLowerCase.VIEWER)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>
                  Viewer {currentRole === RolesLowerCase.VIEWER && <CheckIcon sx={{ height: '12px' }} />}
                </div>
              </RolesButton>

              <RolesButton
                kind={helpFilter ? 'darkPurple' : 'gray'}
                onClick={chooseHelpFilter}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>
                  <EyeIcon style={{ fill: 'black' }} className={styles.previewEye} /> {helpFilter && <CheckIcon sx={{ height: '12px' }} />}
                </div>
              </RolesButton>
            </div>
          </div>

          <div className={styles.toolbarRight}>
            <Tooltip title={'You can send registration link once per 24 hours'}>
              <div>
                <ContainedButton disabled={disableSendAll} className={styles.renotifyAll} onClick={renotifyAll}>
                  {' '}
                  Re-notify All{' '}
                </ContainedButton>
              </div>
            </Tooltip>
            <>
              {userRole === ROLES.ORGANIZATION_ADMIN && (
                <ContainedButton className={styles.addUserIcon} onClick={switchModal}>
                  + Add User
                </ContainedButton>
              )}
            </>
            <>
              {userRole === ROLES.SUPER_ADMIN && (
                <ContainedButton className={styles.addUserIcon} onClick={switchAddSuperAdminModal}>
                  + Add Super Admin
                </ContainedButton>
              )}
            </>
          </div>
        </div>
        <div className={styles.usersListHeader}>
          <OrganizationUsersHeader sortByType={sortByType} sortField={sortField} sortType={sortType} />
        </div>
        <div className={cx(styles.usersList)} ref={listRef}>
          {userList.map((userObj, i) => (
            <div className="mb-5" key={`${userObj.id}`}>
              <SAOrganizationUser toRenotifyUserModal={toRenotifyUserModal} user={userObj} fetchData={fetchData} />
            </div>
          ))}
        </div>
        <PaginationFooter
          page={page}
          total={total}
          onPageChange={onPaginate}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsChange}
        />
      </div>
      <CreateUserModal
        existEmails={userList.map((userObj) => userObj.email)}
        fetchData={fetchData}
        open={showAddModal}
        switchModal={switchModal}
      />
      {userRole === ROLES.SUPER_ADMIN && (
        <CreateSuperAdminModal fetchData={fetchData} open={showAddSuperAdminModal} switchModal={switchAddSuperAdminModal} />
      )}
      <FinishPreviewModal
        resolveCallback={handleResolvedIssue}
        rejectCallback={handleNotResolvedIssue}
        switchModal={switchFinishPreviewModal}
        isOpen={showFinishPreviewModal}
      />
      <RenotifyUserModal renotifyUsers={renotifyUsers} open={!!renotifyUsers} switchModal={closeRenotifyModal} fetchData={fetchData} />
    </div>
  );
};

export default SAUsersList;
